import { useTranslation } from 'react-i18next'

import { useIntersectionObserver } from '#/shared/lib/hooks'
import { ArrowLeftIcon, SearchIcon } from '#/shared/ui/icons'
import { ClearButton } from '#/shared/ui/input'
import { Skeleton } from '#/shared/ui/skeleton'

import type { MessageHitRow } from '../../lib/map-message-hit'
import type { InChatSearch } from '../../model/use-in-chat-search'
import { MessageHitItem } from '../message-hit-item'

/** Header row shown in place of the contact header while searching a thread. */
export function ThreadSearchBar({ search }: { search: InChatSearch }) {
  const { t } = useTranslation()
  return (
    <div className="flex h-16 items-center gap-2 border-b border-warm-100 px-4 py-3">
      <button
        type="button"
        onClick={search.close}
        aria-label={t('phonePanel.conversation.back')}
        className="group flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-paper-avatar text-gray-900 md:size-8"
      >
        <ArrowLeftIcon className="size-4 transition-transform duration-200 group-hover:-translate-x-0.5" />
      </button>
      <div className="flex h-8.5 min-w-0 flex-1 items-center gap-2 rounded-lg border border-gray-200 bg-paper-avatar px-3">
        <SearchIcon className="size-3.5 shrink-0 text-gray-300" />
        <input
          type="text"
          autoFocus
          value={search.input}
          onChange={(event) => search.setInput(event.target.value)}
          placeholder={t('phonePanel.search.inThread')}
          className="input-mobile-safe min-w-0 flex-1 bg-transparent text-sm text-gray-900 placeholder:text-gray-300 focus:outline-none"
        />
        {search.input && <ClearButton onClick={() => search.setInput('')} />}
      </div>
    </div>
  )
}

function HitSkeletons() {
  return (
    <div className="flex flex-col gap-1 py-2">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="flex flex-col gap-1.5 px-4 py-2">
          <Skeleton className="h-3 w-1/5" />
          <Skeleton className="h-2.5 w-4/5" />
        </div>
      ))}
    </div>
  )
}

function HitNotice({ message }: { message: string }) {
  return (
    <div className="px-4 py-8 text-center text-xs text-warm-500">{message}</div>
  )
}

/** Hits inside the open thread, paged as the panel is scrolled. */
function HitList({
  search,
  onOpen,
}: {
  search: InChatSearch
  onOpen: (hit: MessageHitRow) => void
}) {
  const { t } = useTranslation()
  const { result } = search
  const sentinelRef = useIntersectionObserver({
    enabled: result.hasMore && !result.isFetchingMore,
    onIntersect: () => result.loadMore(),
  })
  if (result.isLoading) return <HitSkeletons />
  if (result.isError) {
    return <HitNotice message={t('phonePanel.search.error')} />
  }
  if (result.messages.length === 0) {
    return <HitNotice message={t('phonePanel.search.empty')} />
  }
  return (
    <>
      {result.messages.map((hit) => (
        <MessageHitItem
          key={hit.id}
          hit={hit}
          query={search.query ?? ''}
          hideTitle
          onOpen={onOpen}
        />
      ))}
      {result.isFetchingMore && <HitSkeletons />}
      {result.hasMore && <div ref={sentinelRef} aria-hidden className="h-px" />}
    </>
  )
}

/**
 * Results panel for the in-thread search, hung under the thread header. It
 * scrolls on its own so the timeline underneath keeps its position — picking a
 * hit is what moves it. Hidden until a long-enough term is typed, so opening
 * the box doesn't drop an empty sheet over the conversation.
 */
export function ThreadSearchResults({
  search,
  top,
  bottom,
  onOpen,
}: {
  search: InChatSearch
  /** Height of the frosted header overlay this panel sits under. */
  top: number
  /** Height of the composer dock it must stop above. Measured, not assumed —
   *  the dock grows with attachments, the From row and a multi-line draft, so a
   *  fixed allowance buries the last hit under it. */
  bottom: number
  onOpen: (hit: MessageHitRow) => void
}) {
  if (!search.open || !search.query) return null
  return (
    <div
      style={{ top, maxHeight: `calc(100% - ${top + bottom}px)` }}
      className="absolute inset-x-0 z-20 overflow-y-auto overscroll-contain border-b border-gray-200 bg-white/95 pb-2 shadow-popover backdrop-blur-md"
    >
      <HitList search={search} onOpen={onOpen} />
    </div>
  )
}
